import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  ArrowLeft, Home, Building2, LandPlot, Lock, Unlock, CheckCircle2,
  AlertCircle, XCircle, Sparkles, Eye, FileText, TrendingUp, Shield,
  ArrowRight, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const BIEN_TYPES = [
  { key: 'maison', label: 'Maison individuelle', icon: Home },
  { key: 'immeuble', label: 'Immeuble entier', icon: Building2 },
  { key: 'terrain', label: 'Terrain nu', icon: LandPlot },
];

const ZONES = ['UA', 'UB', 'UC', 'UD', 'UE', 'UI', 'N', 'A', 'Je ne sais pas'];
const CONTRAINTES = [
  { key: 'abf', label: 'Avis ABF / Monument historique' },
  { key: 'lotissement', label: 'Lotissement avec règlement propre' },
  { key: 'copropriete', label: 'Bien en copropriété' },
  { key: 'aucune', label: 'Aucune contrainte identifiée' },
];

const ToolFaisabilite = () => {
  const [inputs, setInputs] = useState({
    bien_type: 'maison',
    surface_terrain: 500,
    surface_bati: 120,
    hauteur: 6,
    nb_pieces: 5,
    ville: '',
    code_postal: '',
    zone_plu: '',
    contraintes: [],
  });
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState({ total_views: 0, total_leads: 0, projets_faisables: 0 });
  const [unlockForm, setUnlockForm] = useState({ name: '', email: '', phone: '', consent: true });
  const [unlocking, setUnlocking] = useState(false);
  const [fullResult, setFullResult] = useState(null);
  const resultRef = useRef(null);

  useEffect(() => {
    axios.post(`${API}/api/faisabilite/track-view`, {}).catch(() => {});
    axios.get(`${API}/api/faisabilite/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setInputs(prev => ({ ...prev, [k]: v }));

  const toggleContrainte = (k) => {
    setInputs(prev => ({
      ...prev,
      contraintes: prev.contraintes.includes(k)
        ? prev.contraintes.filter(c => c !== k)
        : [...prev.contraintes, k],
    }));
  };

  const compute = async () => {
    try {
      const { data } = await axios.post(`${API}/api/faisabilite/compute`, inputs);
      setResult(data);
      setFullResult(null);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (e) {
      toast.error("Erreur lors du calcul");
    }
  };

  const unlock = async () => {
    if (!unlockForm.email || !unlockForm.name) {
      toast.error("Nom et email requis");
      return;
    }
    setUnlocking(true);
    try {
      const { data } = await axios.post(`${API}/api/faisabilite/unlock`, { ...unlockForm, inputs });
      setFullResult(data);
      toast.success("Rapport débloqué !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setUnlocking(false);
    }
  };

  const colorMap = {
    green: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: '#059669', icon: CheckCircle2 },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: '#059669', icon: CheckCircle2 },
    orange: { bg: 'bg-amber-50', text: 'text-amber-700', ring: '#d97706', icon: AlertCircle },
    red: { bg: 'bg-red-50', text: 'text-red-700', ring: '#dc2626', icon: XCircle },
  };

  return (
    <>
      <Helmet>
        <title>Diagnostic Faisabilité Division Immobilière — Puis-je diviser mon bien ? | G Division</title>
        <meta name="description" content="Diagnostic gratuit de faisabilité pour diviser votre bien immobilier (maison, terrain, immeuble). Verdict en 3 minutes + rapport détaillé. Analyse PLU, contraintes et potentiel de gain." />
        <meta name="keywords" content="puis-je diviser mon terrain, faisabilité division, diviser maison autorisation, division parcellaire, comment diviser un bien immobilier, autorisation diviser maison, diviser bien immobilier, division maison 2 logements, étude faisabilité division" />
        <link rel="canonical" href="https://divisionimmobiliere.com/nos-outils/diagnostic-faisabilite-division" />
        <meta property="og:title" content="Diagnostic Faisabilité Division — Puis-je diviser mon bien ? | G Division" />
        <meta property="og:description" content="En 3 minutes, découvrez si votre bien peut être divisé. Score 0-100, verdict, estimation du gain." />
        <script type="application/ld+json">{`{"@context":"https://schema.org","@type":"WebApplication","name":"Diagnostic Faisabilité Division Immobilière","applicationCategory":"BusinessApplication","operatingSystem":"Web","offers":{"@type":"Offer","price":"0","priceCurrency":"EUR"},"description":"Diagnostic gratuit de faisabilité pour la division immobilière","provider":{"@type":"Organization","name":"G Division","url":"https://divisionimmobiliere.com"}}`}</script>
      </Helmet>

      <style>{`
        @keyframes fade-up { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .fade-up { animation: fade-up .5s ease-out both; }
        @keyframes blur-in { from{filter:blur(8px);opacity:.3} to{filter:blur(0);opacity:1} }
        .blur-in { animation: blur-in .6s ease-out both; }
        .locked-blur { filter: blur(6px); pointer-events: none; user-select: none; }
      `}</style>

      <div className="pt-20 bg-gradient-to-b from-gray-50 to-white" data-testid="tool-faisabilite">
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0f1f33]">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.03) 1px,transparent 1px)',
            backgroundSize: '32px 32px'
          }} />
          <div className="absolute -top-20 -left-20 w-[400px] h-[400px] rounded-full"
               style={{ background: 'radial-gradient(circle,rgba(201,162,39,0.25) 0%,transparent 70%)' }} />
          <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
            <Link to="/nos-outils" className="inline-flex items-center text-white/70 hover:text-white mb-5 text-sm">
              <ArrowLeft className="mr-2" size={16} /> Retour aux outils
            </Link>
            <div className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 px-3 py-1.5 rounded-full text-xs font-medium mb-4">
              <Sparkles size={13} /> 100% GRATUIT · Résultat en 3 minutes
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Puis-je diviser mon bien ?<br/>
              <span className="bg-gradient-to-r from-[#e0c062] to-[#c9a227] bg-clip-text text-transparent">
                Diagnostic de faisabilité
              </span>
            </h1>
            <p className="text-gray-300 text-base md:text-lg max-w-2xl mb-5">
              Répondez à 8 questions. Obtenez un score de faisabilité 0-100, un verdict argumenté et une estimation du gain potentiel.
            </p>
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="flex items-center gap-2 bg-white/5 backdrop-blur border border-white/10 px-3 py-2 rounded-xl">
                <Eye size={14} className="text-[#c9a227]" />
                <span className="text-white font-mono font-semibold">{(stats.total_views||0).toLocaleString('fr-FR')}</span>
                <span className="text-white/60 text-xs">diagnostics lancés</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 backdrop-blur border border-white/10 px-3 py-2 rounded-xl">
                <TrendingUp size={14} className="text-emerald-300" />
                <span className="text-white font-mono font-semibold">{stats.projets_faisables || 0}</span>
                <span className="text-white/60 text-xs">projets faisables identifiés</span>
              </div>
            </div>
          </div>
        </section>

        {/* Form */}
        <section className="py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8 fade-up">
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1e3a5f] mb-6">Votre bien</h2>

              {/* Bien type */}
              <div className="mb-6">
                <Label className="text-sm font-medium text-[#1e3a5f] mb-2 block">Type de bien</Label>
                <div className="grid grid-cols-3 gap-3">
                  {BIEN_TYPES.map(t => (
                    <button
                      key={t.key}
                      onClick={() => update('bien_type', t.key)}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${inputs.bien_type === t.key ? 'border-[#c9a227] bg-[#c9a227]/5' : 'border-gray-200 hover:border-gray-300'}`}
                      data-testid={`bien-type-${t.key}`}
                    >
                      <t.icon size={20} className={inputs.bien_type === t.key ? 'text-[#c9a227]' : 'text-gray-400'} />
                      <div className={`mt-2 text-sm font-medium ${inputs.bien_type === t.key ? 'text-[#1e3a5f]' : 'text-gray-700'}`}>{t.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="text-sm">Surface terrain (m²)</Label>
                  <Input type="number" value={inputs.surface_terrain} onChange={e => update('surface_terrain', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">Surface bâtie existante (m²)</Label>
                  <Input type="number" value={inputs.surface_bati} onChange={e => update('surface_bati', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">Hauteur du bâti (m)</Label>
                  <Input type="number" step="0.5" value={inputs.hauteur} onChange={e => update('hauteur', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">Nombre de pièces principales</Label>
                  <Input type="number" value={inputs.nb_pieces} onChange={e => update('nb_pieces', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">Ville</Label>
                  <Input value={inputs.ville} onChange={e => update('ville', e.target.value)} placeholder="Paris" className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">Code postal</Label>
                  <Input value={inputs.code_postal} onChange={e => update('code_postal', e.target.value)} placeholder="75011" className="mt-1" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm">Zone PLU (si connue)</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {ZONES.map(z => (
                      <button key={z} onClick={() => update('zone_plu', z === 'Je ne sais pas' ? '' : z)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${(z === 'Je ne sais pas' && !inputs.zone_plu) || inputs.zone_plu === z ? 'border-[#c9a227] bg-[#c9a227]/10 text-[#c9a227] font-semibold' : 'border-gray-200 text-gray-600'}`}>
                        {z}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm">Contraintes connues</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {CONTRAINTES.map(c => (
                      <button key={c.key} onClick={() => toggleContrainte(c.key)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${inputs.contraintes.includes(c.key) ? 'border-[#c9a227] bg-[#c9a227]/10 text-[#c9a227] font-semibold' : 'border-gray-200 text-gray-600'}`}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <Button onClick={compute} className="w-full bg-[#1e3a5f] hover:bg-[#0f1f33] text-white rounded-full py-6 text-base font-bold uppercase tracking-wider" data-testid="compute-btn">
                Calculer ma faisabilité
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>

            {/* Result */}
            {result && (
              <div ref={resultRef} className="mt-8 fade-up">
                {(() => {
                  const c = colorMap[result.verdict_color] || colorMap.emerald;
                  const Icon = c.icon;
                  const circumference = 2 * Math.PI * 56;
                  const dash = (result.score / 100) * circumference;
                  return (
                    <div className={`${c.bg} rounded-2xl border border-gray-200 p-6 md:p-8`} data-testid="faisabilite-result">
                      <div className="flex flex-col md:flex-row items-center gap-6">
                        <div className="relative w-36 h-36 flex-shrink-0">
                          <svg className="w-full h-full -rotate-90" viewBox="0 0 128 128">
                            <circle cx="64" cy="64" r="56" fill="none" stroke="#e5e7eb" strokeWidth="12" />
                            <circle cx="64" cy="64" r="56" fill="none" stroke={c.ring} strokeWidth="12" strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round" className="transition-all duration-700" />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="text-4xl font-bold text-[#1e3a5f] font-mono">{result.score}</div>
                            <div className="text-xs text-gray-500">/ 100</div>
                          </div>
                        </div>
                        <div className="flex-1 text-center md:text-left">
                          <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                            <Icon size={20} className={c.text} />
                            <span className="text-xs uppercase tracking-wider font-bold text-gray-600">Diagnostic</span>
                          </div>
                          <div className={`text-xl md:text-2xl font-bold ${c.text} font-['Playfair_Display']`}>{result.verdict}</div>
                          <p className="text-sm text-gray-600 mt-2">{result.verdict_desc}</p>
                          <div className="mt-3 inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-gray-200 text-sm">
                            <TrendingUp size={14} className="text-[#c9a227]" />
                            <span className="text-gray-500">Gain estimé :</span>
                            <span className="font-mono font-bold text-[#1e3a5f]">{result.estimated_gain}</span>
                          </div>
                        </div>
                      </div>

                      {/* Preview + gated */}
                      <div className="mt-6 bg-white rounded-xl p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-bold text-[#1e3a5f]">Analyse détaillée</h3>
                          <span className="text-xs text-gray-500">
                            {result.insights_count} points analysés · {result.next_steps_count} étapes recommandées
                          </span>
                        </div>

                        {result.preview_insight && (
                          <div className="mb-3 flex items-start gap-2 text-sm">
                            <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{result.preview_insight.text}</span>
                          </div>
                        )}

                        {!fullResult ? (
                          <>
                            <div className="locked-blur space-y-2 text-sm text-gray-600 mb-4">
                              <div>• Lorem ipsum dolor sit amet consectetur adipiscing elit.</div>
                              <div>• Lorem ipsum dolor sit amet consectetur adipiscing elit.</div>
                              <div>• Lorem ipsum dolor sit amet consectetur adipiscing elit.</div>
                              <div>• Lorem ipsum dolor sit amet consectetur adipiscing elit.</div>
                            </div>
                            <div className="bg-gradient-to-br from-[#1e3a5f] to-[#0f1f33] rounded-xl p-5 text-white mt-4" data-testid="unlock-panel">
                              <div className="flex items-center gap-2 mb-3">
                                <Lock size={16} className="text-[#c9a227]" />
                                <span className="text-xs uppercase tracking-wider text-[#c9a227] font-bold">Débloquez le rapport complet</span>
                              </div>
                              <p className="text-sm text-white/80 mb-4">
                                Insights complets + étapes recommandées + rappel personnalisé de G Division sous 48h. Sans paiement.
                              </p>
                              <div className="grid md:grid-cols-3 gap-2 mb-3">
                                <Input placeholder="Prénom Nom" value={unlockForm.name} onChange={e => setUnlockForm({ ...unlockForm, name: e.target.value })} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="unlock-name" />
                                <Input type="email" placeholder="Email" value={unlockForm.email} onChange={e => setUnlockForm({ ...unlockForm, email: e.target.value })} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="unlock-email" />
                                <Input placeholder="Téléphone" value={unlockForm.phone} onChange={e => setUnlockForm({ ...unlockForm, phone: e.target.value })} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="unlock-phone" />
                              </div>
                              <Button onClick={unlock} disabled={unlocking} className="w-full bg-[#c9a227] hover:bg-[#b08d22] text-white rounded-full py-5 font-bold uppercase tracking-wider text-xs" data-testid="unlock-btn">
                                {unlocking ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Unlock size={14} className="mr-2" />}
                                Débloquer mon rapport
                              </Button>
                              <div className="mt-2 text-xs text-white/50 flex items-center gap-1">
                                <Shield size={10} /> Vos données restent confidentielles.
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="blur-in space-y-3">
                            <h4 className="font-bold text-[#1e3a5f] mt-3">Analyse (tous les points)</h4>
                            <ul className="space-y-2 text-sm">
                              {fullResult.insights.map((ins, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  {ins.type === 'positive' ? <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" /> :
                                    ins.type === 'warning' ? <AlertCircle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" /> :
                                    <XCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />}
                                  <span className="text-gray-700">{ins.text}</span>
                                </li>
                              ))}
                            </ul>
                            <h4 className="font-bold text-[#1e3a5f] mt-4">Prochaines étapes recommandées</h4>
                            <ol className="space-y-2 text-sm">
                              {fullResult.next_steps.map((step, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <span className="w-5 h-5 flex-shrink-0 rounded-full bg-[#c9a227]/10 text-[#c9a227] text-xs font-bold flex items-center justify-center">{i+1}</span>
                                  <span className="text-gray-700">{step}</span>
                                </li>
                              ))}
                            </ol>
                            <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900">
                              <strong>Rappel sous 48h :</strong> Un expert G Division vous contactera au {unlockForm.phone || "numéro communiqué"} pour affiner votre projet.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </section>

        {/* SEO content */}
        <section className="py-12 md:py-16 bg-white border-t border-gray-100">
          <div className="max-w-3xl mx-auto px-4 md:px-8 prose text-gray-700">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-6">Diviser son bien immobilier : ce qu'il faut savoir</h2>
            <p>
              La <strong>division immobilière</strong> consiste à transformer un bien unique (maison, immeuble, terrain) en plusieurs lots distincts, chacun susceptible d'être vendu ou loué séparément. C'est l'une des stratégies de valorisation les plus puissantes du marché, mais elle est encadrée par de nombreuses règles d'urbanisme.
            </p>
            <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1e3a5f] mt-8 mb-3">Les 3 types de division</h3>
            <ul>
              <li><strong>Division parcellaire</strong> : séparer un terrain en plusieurs parcelles indépendantes.</li>
              <li><strong>Division horizontale</strong> : créer plusieurs logements à partir d'une maison existante (étages séparés).</li>
              <li><strong>Division verticale</strong> : scinder le bâti en lots côte à côte (duplex, maison double).</li>
            </ul>
            <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1e3a5f] mt-8 mb-3">Autorisations nécessaires</h3>
            <p>
              Selon l'ampleur du projet : <strong>déclaration préalable (DP)</strong> pour les divisions parcellaires simples sans création de voirie, <strong>permis de construire (PC)</strong> si création d'ouvertures ou extensions, <strong>permis d'aménager (PA)</strong> si création de voirie ou plus de 2 lots en zone sensible.
            </p>
            <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1e3a5f] mt-8 mb-3">Pourquoi utiliser cet outil ?</h3>
            <p>
              Avant de lancer une étude payante avec un architecte (3-5 k€) ou un géomètre (1-2 k€), ce diagnostic gratuit vous donne une première indication fiable. Un score &gt; 55 justifie une étude approfondie — en-dessous, mieux vaut envisager d'autres stratégies de valorisation.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default ToolFaisabilite;
